import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { adminGetBookings, adminSendInvoice } from "@/lib/api";
import { toast } from "sonner";
import { Printer, ArrowLeft, Send, Snowflake } from "lucide-react";
import { parseISODate } from "@/lib/format";

const TOKEN_KEY = "ice_admin_token";
const PRICE_PER_CHILD = 14;
const LOGO_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_9ec7afdd-8040-4034-8218-64e5a74fdccb/artifacts/8npbah8c_COI%20-%20Small%20Logo.png";
const WEEKDAY_LONG = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const humanDate = (iso) => {
  if (!iso) return "";
  const d = parseISODate(iso);
  return `${WEEKDAY_LONG[(d.getDay() + 6) % 7]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const formatEuro = (n) => `€${Number(n || 0).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const invoiceNumber = (bs) => {
  if (!bs || bs.length === 0) return "";
  const first = (bs[0].reference || "").replace("COI-", "").replace("SOI-", "");
  return bs.length === 1 ? `INV-${first}` : `INV-M-${first}-${bs.length}`;
};

const issuedDate = () => {
  const d = new Date();
  return `${WEEKDAY_LONG[(d.getDay() + 6) % 7]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export default function AdminInvoicePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isMulti = id === "multi";
  const multiIds = useMemo(
    () => (searchParams.get("ids") || "").split(",").filter(Boolean),
    [searchParams]
  );

  const [token] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const all = await adminGetBookings(token);
        if (isMulti) {
          const order = new Map(multiIds.map((x, i) => [x, i]));
          const bs = all
            .filter((b) => order.has(b.id))
            .sort((a, b) => order.get(a.id) - order.get(b.id));
          setBookings(bs);
        } else {
          const b = all.find((x) => x.id === id);
          setBookings(b ? [b] : []);
        }
      } catch (e) {
        toast.error("Failed to load booking");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, id, isMulti, multiIds]);

  const primary = bookings[0];
  const allSameSchool = bookings.length > 0 && bookings.every((b) => (b.school_name || "").trim() === (primary?.school_name || "").trim());
  const subtotal = bookings.reduce((n, b) => n + (b.group_size || 0) * PRICE_PER_CHILD, 0);
  const total = subtotal;

  const sendEmail = async () => {
    if (!primary) return;
    if (!window.confirm(`Email invoice ${invoiceNumber(bookings)} to ${primary.email}?`)) return;
    try {
      setSending(true);
      const res = await adminSendInvoice(token, primary.id);
      toast.success(`Invoice emailed (id ${res.email_id?.slice(-6) || "ok"})`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send invoice";
      toast.error(typeof msg === "string" ? msg : "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <p className="font-mono-tight text-sm text-slate-600 mb-4">Admin sign-in required.</p>
          <Link to="/admin" className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 inline-block">
            Go to admin login
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-16 text-center font-mono-tight uppercase tracking-widest">Loading…</div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <p className="font-mono-tight text-sm text-slate-600 mb-4">{isMulti ? "No matching bookings." : "Booking not found."}</p>
          <Link to="/admin" className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 inline-block">
            Back to admin
          </Link>
        </div>
      </div>
    );
  }

  const poNumbersHeader = bookings.map((b) => b.po_number).filter(Boolean);
  const showPoInHeader = !isMulti && !!primary.po_number;

  return (
    <div className="min-h-screen bg-slate-50 print-clean">
      <div className="no-print"><Header /></div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 no-print">
          <Link
            to="/admin"
            className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2 flex items-center gap-2"
            data-testid="invoice-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Admin
          </Link>
          <div className="flex items-center gap-2">
            {!isMulti && (
              <button
                onClick={sendEmail}
                disabled={sending}
                className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2 flex items-center gap-2 disabled:opacity-60"
                data-testid="invoice-email"
              >
                <Send className="w-3.5 h-3.5" strokeWidth={2.5} /> {sending ? "Sending…" : "Email invoice"}
              </button>
            )}
            <button
              onClick={() => window.print()}
              className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="invoice-print"
            >
              <Printer className="w-4 h-4" strokeWidth={2.5} /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Invoice sheet */}
        <div className="bg-white border-2 border-slate-900 brutal-shadow print:border-0 print:shadow-none" data-testid="invoice-sheet">
          {/* Header */}
          <div className="flex items-start justify-between p-8 border-b-2 border-slate-900 print:p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 border-2 border-slate-900 overflow-hidden bg-white">
                <img src={LOGO_URL} alt="Clonmel On Ice" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-display text-2xl uppercase leading-none">Clonmel On Ice</div>
                <div className="font-mono-tight text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-1">
                  Rink Booking 2026/27
                </div>
                <div className="font-mono-tight text-xs text-slate-600 mt-3">
                  clonmelonice@gmail.com
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-block bg-slate-900 text-white px-4 py-1 font-display uppercase text-lg">
                Invoice
              </div>
              <div className="font-mono-tight text-xs text-slate-500 mt-3 uppercase tracking-widest">Invoice #</div>
              <div className="font-mono-tight text-sm font-bold" data-testid="invoice-number">{invoiceNumber(bookings)}</div>
              <div className="font-mono-tight text-xs text-slate-500 mt-3 uppercase tracking-widest">Issued</div>
              <div className="font-mono-tight text-sm">{issuedDate()}</div>
              {showPoInHeader && (
                <>
                  <div className="font-mono-tight text-xs text-slate-500 mt-3 uppercase tracking-widest">PO Number</div>
                  <div className="font-mono-tight text-sm font-bold">{primary.po_number}</div>
                </>
              )}
              {isMulti && poNumbersHeader.length > 0 && (
                <>
                  <div className="font-mono-tight text-xs text-slate-500 mt-3 uppercase tracking-widest">PO Number{poNumbersHeader.length > 1 ? "s" : ""}</div>
                  <div className="font-mono-tight text-sm font-bold">{poNumbersHeader.join(", ")}</div>
                </>
              )}
            </div>
          </div>

          {/* Bill to + Session (or bill-to-only for multi with different schools) */}
          <div className="grid md:grid-cols-2 gap-0 border-b-2 border-slate-900">
            <div className="p-8 border-r-2 border-slate-900 print:p-6">
              <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-3">Bill to</div>
              {allSameSchool ? (
                <>
                  <div className="font-bold text-lg text-slate-900">{primary.school_name}</div>
                  <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{primary.school_address}</div>
                  <div className="text-sm text-slate-700">{primary.county}, Ireland</div>
                  <div className="border-t border-slate-200 mt-4 pt-3 text-sm text-slate-700">
                    <div><span className="font-mono-tight text-xs text-slate-500 uppercase tracking-widest">Contact</span> · {primary.teacher_name}</div>
                    <div className="font-mono-tight text-xs mt-1">{primary.email}</div>
                    <div className="font-mono-tight text-xs">{primary.phone}</div>
                  </div>
                </>
              ) : (
                <div className="space-y-1" data-testid="invoice-multi-schools">
                  {bookings.map((b) => (
                    <div key={b.id} className="font-bold text-slate-900">{b.school_name}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-8 print:p-6">
              {isMulti ? (
                <>
                  <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-3">Summary</div>
                  <div className="font-bold text-lg text-slate-900">{bookings.length} bookings</div>
                  <div className="font-mono-tight text-sm text-slate-600 mt-1">
                    {bookings.reduce((n, b) => n + (b.group_size || 0), 0)} pupils · {bookings.length * 45} minutes total
                  </div>
                  <div className="border-t border-slate-200 mt-4 pt-3 text-sm text-slate-700 space-y-1">
                    <div><span className="font-mono-tight text-xs text-slate-500 uppercase tracking-widest">References</span></div>
                    <div className="font-mono-tight text-xs font-bold break-all">
                      {bookings.map((b) => b.reference).join(", ")}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-3">Session</div>
                  <div className="font-bold text-lg text-slate-900">{humanDate(primary.date)}</div>
                  <div className="font-mono-tight text-2xl text-sky-600 font-bold mt-1">{primary.time} <span className="text-sm text-slate-500">(45 min)</span></div>
                  <div className="border-t border-slate-200 mt-4 pt-3 text-sm text-slate-700 space-y-1">
                    <div><span className="font-mono-tight text-xs text-slate-500 uppercase tracking-widest">Reference</span> · <span className="font-mono-tight font-bold">{primary.reference}</span></div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Line items */}
          <table className="w-full">
            <thead className="bg-slate-900 text-white font-mono-tight uppercase text-[11px] tracking-widest print:bg-slate-100 print:text-slate-900">
              <tr>
                <th className="text-left px-6 py-3">Description</th>
                <th className="text-right px-6 py-3 w-24">Qty</th>
                <th className="text-right px-6 py-3 w-32">Rate</th>
                <th className="text-right px-6 py-3 w-32">Amount</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t-2 border-slate-900" data-testid="invoice-line-item">
                  <td className="px-6 py-5">
                    <div className="font-bold text-slate-900">{b.teacher_name}</div>
                    <div className="text-sm text-slate-600 mt-1">
                      {isMulti && !allSameSchool ? `${b.school_name} · ` : ""}{humanDate(b.date)} at {b.time}
                    </div>
                    <div className="text-xs text-slate-500 font-mono-tight mt-1">
                      Ref {b.reference}
                      {b.po_number ? ` · PO ${b.po_number}` : ""}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right font-mono-tight font-bold text-lg">{b.group_size}</td>
                  <td className="px-6 py-5 text-right font-mono-tight">{formatEuro(PRICE_PER_CHILD)}</td>
                  <td className="px-6 py-5 text-right font-mono-tight font-bold">{formatEuro((b.group_size || 0) * PRICE_PER_CHILD)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-8 border-t-2 border-slate-900 print:p-6" />
            <div className="border-t-2 border-slate-900 border-l-0 md:border-l-2">
              <table className="w-full">
                <tbody className="font-mono-tight">
                  <tr>
                    <td className="px-8 py-3 uppercase text-xs text-slate-500 tracking-widest">Subtotal</td>
                    <td className="px-8 py-3 text-right" data-testid="invoice-subtotal">{formatEuro(subtotal)}</td>
                  </tr>
                  <tr>
                    <td className="px-8 py-3 uppercase text-xs text-slate-500 tracking-widest">VAT (0%)</td>
                    <td className="px-8 py-3 text-right">{formatEuro(0)}</td>
                  </tr>
                  <tr className="bg-sky-500 text-white">
                    <td className="px-8 py-4 uppercase text-sm tracking-widest font-bold">Total due</td>
                    <td className="px-8 py-4 text-right text-2xl font-bold" data-testid="invoice-total">{formatEuro(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Company details */}
          <div className="border-t-2 border-slate-900 p-8 print:p-6 text-slate-800" data-testid="invoice-company-footer">
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Company</div>
                <div className="font-bold">Profile Music Group Ltd</div>
                <div className="text-slate-700 mt-1">Unit 6, Tenure Business Park, Tenure,<br />Co. Louth, A92 NF29</div>
                <div className="font-mono-tight text-xs text-slate-600 mt-2">CRO: 487637 · VAT: IE9757257P</div>
                <div className="font-mono-tight text-xs text-slate-600">clonmelonice@gmail.com</div>
              </div>
              <div>
                <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Payment details</div>
                <div className="text-slate-700">Bank of Ireland, Dunboyne, Co. Meath</div>
                <div className="font-mono-tight text-xs mt-2"><span className="text-slate-500">IBAN</span> · <span className="font-bold">IE28BOFI90334995505369</span></div>
                <div className="font-mono-tight text-xs"><span className="text-slate-500">BIC</span> · <span className="font-bold">BOFIIE2DXXX</span></div>
              </div>
            </div>
          </div>

          {/* Support contact */}
          <div className="border-t-2 border-slate-900 px-8 py-4 print:px-6 print:py-3 text-center text-sm text-slate-700" data-testid="invoice-support-line">
            Any questions? Email us at{" "}
            <a href="mailto:clonmelonice@gmail.com" className="font-mono-tight font-bold text-slate-900 underline">clonmelonice@gmail.com</a>
          </div>

          {/* Footer */}
          <div className="p-4 border-t-2 border-slate-900 bg-slate-900 text-white flex items-center justify-between text-[10px] font-mono-tight uppercase tracking-widest print:bg-slate-100 print:text-slate-900">
            <span className="flex items-center gap-2"><Snowflake className="w-3 h-3" strokeWidth={3} /> Clonmel On Ice · 2026/27</span>
            <span>Thank you for your booking</span>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-clean { background: #fff !important; }
          [data-testid="invoice-sheet"] { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
